import React, { useCallback, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import Slider from "react-slick";

// local component imports
import { CarouselArrow } from "./Arrow";
import Dots from "./Dots";
import DesktopDot from "./DesktopDot";
import FreeFlowCarousel from "./freeFlowCarousel";
import MediaCell from "./MediaCell";
import CellCaption from "../CellCaption";

// custom hook imports
import useHorizontalScrollForCarousel from "../../../../../customHooks/horizontalScroll";
import useSlickSlideInert from "../../../../../customHooks/useSlickSlideInert";
import { shouldPrioritizeCarouselCell } from "../../../../../utils/dynamicHomeUtils/lcpWidget";

// slick css imports
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

/**
 * DynamicCarousel renders a configurable carousel with image, video, and Lottie animation support.
 * @param {object} props - Component props.
 * @param {object} props.layoutSnapshot - Layout configuration including carousel settings.
 * @param {object} props.sectionSettings - Section-level settings for border, text style, etc.
 * @param {boolean} props.isAboveFold - Whether this carousel is expected above the fold.
 * @param {boolean} props.isLcpCandidate - Whether this carousel may contain the LCP image.
 * @returns {React.ReactElement|null} The carousel or null if no valid cells exist.
 */
const DynamicCarousel = ({
  layoutSnapshot,
  sectionSettings,
  isMobile,
  isAboveFold = false,
  isLcpCandidate = false,
}) => {
  const carouselConfig = layoutSnapshot?.carouselConfig;

  const sliderRef = useRef(null);
  const carouselContainerRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [playingVideos, setPlayingVideos] = useState(new Set());
  const [loadedVideos, setLoadedVideos] = useState(new Set());
  const videoRefs = useRef(new Map());

  useHorizontalScrollForCarousel(sliderRef, isHovered, isMobile);
  useSlickSlideInert(carouselContainerRef, currentSlide);

  /** Sets hovered state to true when mouse enters the carousel. */
  const onMouseEnter = () => setIsHovered(true);
  /** Sets hovered state to false when mouse leaves the carousel. */
  const onMouseLeave = () => setIsHovered(false);

  const cells = useMemo(() => {
    const allRows = carouselConfig?.grid?.rows ?? [];
    return allRows.flatMap((row) => row.cells ?? []).sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  }, [carouselConfig?.grid?.rows]);

  const gridConfig = carouselConfig?.grid;
  const options = carouselConfig?.options ?? {};
  const showDots = options?.dots;
  const { columnGap } = gridConfig || {};
  const slideGap = columnGap ?? 15;
  const effectiveSlideGap = (options.slidesToShow ?? 1) <= 1 && !(options.center ?? false) ? 0 : slideGap;
  const borderRadius = sectionSettings?.gridBorder?.borderRadius ?? 0;
  const borderThickness = sectionSettings?.gridBorder?.borderThickness ?? 0;
  const borderColor = sectionSettings?.gridBorder?.borderColor ?? "transparent";
  const gridTextStyle = sectionSettings?.gridTextStyle || {};
  const {
    textEnabled = false,
    innerText = false,
    fontSize: textFontSize,
    fontWeight: textFontWeight,
    fontFamily: textFontFamily,
    titleColor: textColor,
    horizontalAlignment = "center",
    verticalAlignment = "bottom",
  } = gridTextStyle;

  const textStyle = {
    fontSize: textFontSize ? `${textFontSize}px` : undefined,
    fontWeight: textFontWeight || undefined,
    fontFamily: textFontFamily || undefined,
    color: textColor || undefined,
    textAlign: horizontalAlignment,
  };

  const sectionPadding = {
    paddingLeft: `${sectionSettings?.padding?.left || 0}px`,
    paddingRight: `${sectionSettings?.padding?.right || 0}px`,
  };

  const verticalPositionClass =
    verticalAlignment === "top" ? "top-0" : verticalAlignment === "center" ? "top-1/2 -translate-y-1/2" : "bottom-0";
  const overlayTopPaddingClass = verticalAlignment === "top" ? "pt-[8px] md:pt-[12px]" : "";

  const validCells = useMemo(() => cells.filter((cell) => cell.imageUrl || cell.mediaUrl), [cells]);

  const slidesToShow = options.slidesToShow ?? 1;
  const isCenterMode = options.center ?? false;
  // react-slick only supports slidesToScroll === 1 while centerMode is on; it warns and
  // misaligns the centered slide otherwise, so clamp instead of trusting the CMS value.
  const slidesToScroll = isCenterMode ? 1 : (options.slidesToScroll ?? 1);
  const rowCount = options.rows ?? 1;
  const shouldShowDots = showDots && slidesToShow < 1.1;
  const showSlider = slidesToShow > 1.1 && showDots;
  const totalSlides = Math.ceil(validCells.length / slidesToShow);
  const scrollProgress =
    validCells.length > 0 ? Math.min(((slidesToShow + currentSlide) / validCells.length) * 100, 100) : 100;
  const aspectRatio = gridConfig?.rows?.[0]?.aspectRatio ?? 1;

  /**
   * Toggles play/pause for the video at the given index.
   * @param {number} index - The cell index of the video.
   */
  const onToggleVideoPlay = useCallback(
    (index) => {
      const video = videoRefs.current.get(index);
      if (!video) return;
      // playingVideos is driven by the element's own play/pause events, so only drive the element here.
      if (playingVideos.has(index)) video.pause();
      else video.play()?.catch(() => {});
    },
    [playingVideos],
  );

  /**
   * Updates current slide index before a slide transition.
   * @param {number} _current - The current slide index.
   * @param {number} next - The next slide index.
   */
  const onBeforeChange = useCallback((_current, next) => setCurrentSlide(next), []);

  /**
   * Navigates the slider to the given index.
   * @param {number} index - The target slide index.
   */
  const onGoToSlide = useCallback((index) => sliderRef.current?.slickGoTo(index), []);

  if (!validCells.length) return null;

  const prefersReducedMotion =
    typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const paddingLeft = sectionSettings?.padding?.left || 0;
  const paddingRight = sectionSettings?.padding?.right || 0;
  const adjustedPaddingLeft = `${Math.max(0, Math.round(paddingLeft - effectiveSlideGap / 2))}px`;
  const arrowLeftPos = `${24 - paddingLeft}px`;
  const arrowRightPos = `${24 - paddingRight}px`;

  const sliderSettings = {
    infinite: options.infinite ?? false,
    speed: options.speed ?? 400,
    slidesToShow,
    slidesToScroll,
    arrows: isMobile ? false : validCells.length > slidesToShow,
    dots: false,
    nextArrow: <CarouselArrow direction="next" rightPos={arrowRightPos} />,
    prevArrow: <CarouselArrow direction="prev" leftPos={arrowLeftPos} />,
    beforeChange: onBeforeChange,
    autoplay: prefersReducedMotion ? false : (options.autoplay ?? false),
    autoplaySpeed: options.autoplaySpeed ?? 3000,
    pauseOnHover: true,
    pauseOnFocus: true,
    rows: rowCount,
    centerMode: isCenterMode,
    // Wider side peeks than the previous 6% so neighboring banners are more visible
    centerPadding: isCenterMode ? (isMobile ? "6%" : "10%") : "0px",
    adaptiveHeight: false,
  };

  // Free-flow native scroll on mobile when partially showing multiple slides
  if (isMobile && slidesToShow > 1.1) {
    return (
      <FreeFlowCarousel
        validCells={validCells}
        slidesToShow={slidesToShow}
        slideGap={slideGap}
        rowCount={rowCount}
        rowGap={gridConfig?.rowGap ?? 12}
        aspectRatio={aspectRatio}
        borderRadius={borderRadius}
        gridTextStyle={gridTextStyle}
        sectionPadding={sectionPadding}
        borderThickness={borderThickness}
        borderColor={borderColor}
        isShowSlider={validCells.length > slidesToShow && showDots}
        isMobile={isMobile}
        isAuto={options.autoplay}
        isCenter={isCenterMode}
        isAboveFold={isAboveFold}
        isLcpCandidate={isLcpCandidate}
      />
    );
  }

  return (
    <div
      ref={carouselContainerRef}
      className="dynamic-carousel w-full overflow-hidden"
      style={{
        paddingLeft: adjustedPaddingLeft,
        paddingRight: sectionPadding?.paddingRight,
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <Slider ref={sliderRef} {...sliderSettings}>
        {validCells.map((cell, index) => {
          const textContent = cell.textContent || cell.title;

          /**
           * Applies hover border and shadow on mouse enter.
           * @param {React.MouseEvent} e - The mouse event.
           */
          const onCellMouseEnter = (e) => {
            if (!isMobile) {
              e.currentTarget.style.border = "2px solid rgba(255, 255, 255, 0.80)";
              e.currentTarget.style.boxShadow = "0 2px 16px 0 rgba(0, 0, 0, 0.12)";
            }
          };

          /**
           * Resets border and shadow on mouse leave.
           * @param {React.MouseEvent} e - The mouse event.
           */
          const onCellMouseLeave = (e) => {
            if (!isMobile) {
              e.currentTarget.style.border = "2px solid transparent";
              e.currentTarget.style.boxShadow = "none";
            }
          };

          return (
            <div
              id={`dynamic-carousel-cell-${index}`}
              key={`cell-${index}`}
              className="!pr-0 align-top outline-none"
              style={{ padding: isMobile ? undefined : "4px", margin: isMobile ? undefined : "4px" }}
            >
              <div
                className="relative overflow-hidden"
                style={{
                  marginLeft: `${effectiveSlideGap / 2}px`,
                  marginRight: `${effectiveSlideGap / 2}px`,
                  width: effectiveSlideGap === 0 ? "100%" : undefined,
                  transition: "border 0.2s ease, box-shadow 0.2s ease",
                  border: isMobile ? undefined : "2px solid transparent",
                  borderRadius: `${borderRadius}px`,
                  marginTop: isMobile ? "0px" : "20px",
                  marginBottom: isMobile ? "0px" : "20px",
                }}
                onMouseEnter={onCellMouseEnter}
                onMouseLeave={onCellMouseLeave}
              >
                <MediaCell
                  cell={cell}
                  index={index}
                  borderRadius={borderRadius}
                  borderThickness={borderThickness}
                  borderColor={borderColor}
                  aspectRatio={aspectRatio}
                  loadedVideos={loadedVideos}
                  videoRefs={videoRefs}
                  onToggleVideoPlay={onToggleVideoPlay}
                  setLoadedVideos={setLoadedVideos}
                  setPlayingVideos={setPlayingVideos}
                  isEager={shouldPrioritizeCarouselCell(index, isAboveFold)}
                  isPriority={shouldPrioritizeCarouselCell(index, isLcpCandidate)}
                />
                {textEnabled && textContent && innerText && (
                  <div
                    className={`absolute left-0 right-0 px-[15px] pb-[${isMobile ? 8 : 15}px] md:px-[10px] ${verticalPositionClass} ${overlayTopPaddingClass}`}
                  >
                    <p className="break-words" style={textStyle}>
                      {textContent}
                    </p>
                  </div>
                )}
              </div>
              {textEnabled && textContent && !innerText && (
                <CellCaption
                  style={textStyle}
                  wrapperStyle={{
                    // marginLeft: `${effectiveSlideGap / 2}px`,
                    // marginRight: `${effectiveSlideGap / 2}px`,
                    marginTop: isMobile ? "8px" : "-4px",
                  }}
                >
                  {textContent}
                </CellCaption>
              )}
            </div>
          );
        })}
      </Slider>
      {shouldShowDots && isMobile && (
        <Dots currentSlide={currentSlide} totalSlides={totalSlides} onDotClick={onGoToSlide} />
      )}
      {shouldShowDots && !isMobile && (
        <DesktopDot
          currentSlide={currentSlide}
          totalSlides={totalSlides}
          onDotClick={onGoToSlide}
          isAutoplay={options.autoplay ?? false}
          autoplaySpeed={options.autoplaySpeed ?? 3000}
        />
      )}
      {showSlider && !isMobile && (
        <div className="mx-auto mt-[32px] w-full max-w-[30vw]">
          <div
            className="block h-[4px] w-full overflow-hidden rounded bg-[#E0E0E0]"
            role="progressbar"
            aria-valuemin="0"
            aria-valuemax="100"
            aria-valuenow={scrollProgress}
            style={{
              backgroundImage: "linear-gradient(to right, #737530, #737530)",
              backgroundRepeat: "no-repeat",
              backgroundSize: `${scrollProgress}% 100%`,
              transition: "background-size 0.2s ease",
            }}
          >
            <span className="sr-only">{Math.round(scrollProgress)}% scrolled</span>
          </div>
        </div>
      )}
    </div>
  );
};

DynamicCarousel.propTypes = {
  layoutSnapshot: PropTypes.object,
  sectionSettings: PropTypes.object,
  isMobile: PropTypes.bool.isRequired,
  isAboveFold: PropTypes.bool,
  isLcpCandidate: PropTypes.bool,
};

export default DynamicCarousel;
